<template>
  <div class="person-details evollu-form">

    <div class="row">
      <div class="col-12 col-md-2">
        <va-input
          :placeholder="$t('clinical.people.title')"
          v-model="form.title"
          id="editing-title"
          :error="formErrors.has('title')"
          :error-messages="[$t(`${formErrors.get('title')}`)]"
          @input="formErrors.clear('title')"
          :disabled="!editing"
          :maxlength="50"
          required/>
      </div>

      <div class="col-12 col-md-5">
        <va-input
          :placeholder="$t('clinical.people.first_name')"
          v-model="form.first_name"
          id="editing-first-name"
          :class="{'red-border': formErrors.has('first_name')}"
          :error="formErrors.has('first_name')"
          :error-messages="[$t(`${formErrors.get('first_name')}`)]"
          @input="formErrors.clear('first_name')"
          :disabled="!editing"
          :maxlength="255"
          required
        />
      </div>

      <div class="col-12 col-md-5">
        <va-input
          :placeholder="$t('references.inputs.surname')"
          v-model="form.last_name"
          :class="{'red-border': formErrors.has('last_name')}"
          id="editing-last-name"
          :error="formErrors.has('last_name')"
          :error-messages="[$t(`${formErrors.get('last_name')}`)]"
          @input="formErrors.clear('last_name')"
          :disabled="!editing"
          :maxlength="255"
          required
        />
      </div>

    </div>

    <div class="row">
      <div class="col-12 col-md-5">
        <va-input
          id="successfulEmail"
          name="successfulEmail"
          v-model="form.email"
          :placeholder="$t('auth.email')"
          :error="invalidEmail || formErrors.has('email')"
          :error-messages="[$t('people.errors.email_must_be_a_valid_email')]"
          :disabled="!editing"
        />
      </div>
      <div class="col-12 col-md-4">
        <va-input
          :placeholder="$t('people.inputs.birthdate')"
          v-model="birthdate"
          id="editing-birthdate"
          :error="invalidBirthdate || formErrors.has('birthdate')"
          :error-messages="[birthdateError]"
          @input="formErrors.clear('birthdate')"
          :disabled="!editing"
          required/>
      </div>
      <div class="col-12 col-md-3">
        <va-input
          :placeholder="$t('clinical.people.client_nr')"
          v-model="form.process_id"
          id="editing-process-id"
          :error="formErrors.has('process_id')"
          :error-messages="[$t(`${formErrors.get('process_id')}`)]"
          @input="formErrors.clear('process_id')"
          :disabled="!editing"
          :maxlength="255"
          required>
          <span slot="append">
            <va-popover
              placement="top"
              :message="$t('people.inputs.fill_client_nr')"
            >
              <va-icon
                class="clickable"
                name="fa fa-info-circle"
              />
            </va-popover>
          </span>
        </va-input>
      </div>
    </div>

    <div class="row" v-if="!selectedPerson && !entity ">
      <div class="col-12">
        <multiselect
          v-model="form.entity"
          :options="availableEntities"
          track-by="id"
          label="name"
          select-label=""
          selected-label=""
          deselect-label=""
          :placeholder="$t('people.inputs.entity')"
          :allow-empty="true"
          :multiple="false"
          :searchable="true"
          :disabled="availableEntities.length === 1"
          @input="formErrors.clear('entity_id')"
          >
          <template slot="option" slot-scope="{option}">
            <span>{{option.name}}</span>
            <span v-if="option.cities && option.cities.length">
              ({{option.cities.join(',')}})
            </span>
          </template>
          <template slot="singleLabel" slot-scope="{option}">
            <span>{{option.name}}</span>
            <span v-if="option.cities && option.cities.length">
              ({{option.cities.join(',')}})
            </span>
          </template>
        </multiselect>
        <span class="small text-danger" v-show="formErrors.has('entity_id')">
          {{ $t(`${formErrors.get('entity_id')}`) }}
        </span>
      </div>
    </div>

    <div class="row">
      <div class="col-12 col-md-4">
        <multiselect
          v-model="form.gender"
          :options="genders"
          track-by="id"
          label="label"
          select-label=""
          selected-label=""
          deselect-label=""
          :placeholder="$t('people.inputs.gender')"
          :allow-empty="false"
          :multiple="false"
          :searchable="false"
          :disabled="!editing"
          >
        </multiselect>
      </div>
      <div class="col-12 col-md-4">
        <vue-tel-input
          inputId="phone"
          style="width: inherit;"
          :key="vueTelInputRedrawIndex"
          v-model="form.phone"
          maxlength="20"
          :preferredCountries="countries.allowed"
          :disabledFetchingCountry="true"
          :defaultCountry="defaultCountrySelection"
          :disabled="!editing"
          :placeholder="$t('people.inputs.phone')"
          @validate="telephoneValidation">
        </vue-tel-input>
        <span class="small text-danger" v-show="form.phone !== '' && form.phone !== null && invalidPhone">
          {{ $t('patients.errors.invalidPhone') }}
        </span>
      </div>
      <div class="col-12 col-md-4">
        <vue-tel-input
          inputId="mobile-phone"
          style="width: inherit;"
          :key="vueTelInputRedrawIndex"
          v-model="form.mobile_phone"
          maxlength="20"
          :preferredCountries="countries.allowed"
          :disabledFetchingCountry="true"
          :defaultCountry="defaultCountrySelection"
          :disabled="!editing"
          :placeholder="$t('people.inputs.mobile-phone')"
          @validate="mobilePhoneValidation">
        </vue-tel-input>
        <span class="small text-danger" v-show="form.mobile_phone !== '' && form.mobile_phone !== null && invalidMobilePhone">
          {{ $t('patients.errors.invalidPhone') }}
        </span>
      </div>
    </div>

    <div class="row">

      <div class="col-12 col-md-6">
        <va-input
          :placeholder="$t('clinical.people.address')"
          v-model="form.addresses[0].line1"
          id="editing-address-line1"
          :error="formErrors.has('address.line1')"
          :error-messages="[$t(`${formErrors.get('address.line1')}`)]"
          @input="formErrors.clear('address.line1')"
          :disabled="!editing"
          :maxlength="255"
          required/>
      </div>

      <div class="col-12 col-md-3">
        <va-input
          :placeholder="$t('clinical.people.postal_code')"
          v-model="form.addresses[0].postal_code"
          id="editing-address-postal-code"
          :error="formErrors.has('address.postal_code')"
          :error-messages="[$t(`${formErrors.get('address.postal_code')}`)]"
          @input="formErrors.clear('address.postal_code')"
          :disabled="!editing"
          :maxlength="255"
          required/>
      </div>

      <div class="col-12 col-md-3">
        <va-input
          :placeholder="$t('clinical.people.city')"
          v-model="form.addresses[0].city"
          id="editing-address-city"
          :error="formErrors.has('address.city')"
          :error-messages="[$t(`${formErrors.get('address.city')}`)]"
          @input="formErrors.clear('address.city')"
          :disabled="!editing"
          :maxlength="255"
          required/>
      </div>

    </div>

    <div class="row">
      <div class="col-12 col-md-6">
        <va-input
          :placeholder="$t('clinical.people.doctor')"
          v-model="form.doctor"
          id="editing-doctor"
          :error="formErrors.has('doctor')"
          :error-messages="[$t(`${formErrors.get('doctor')}`)]"
          @input="formErrors.clear('doctor')"
          :disabled="!editing"
          :maxlength="255"
          required/>
      </div>

      <div class="col-12 col-md-6">
        <va-input
          :placeholder="$t('clinical.people.profession')"
          v-model="form.profession"
          id="editing-profession"
          :error="formErrors.has('profession')"
          :error-messages="[$t(`${formErrors.get('profession')}`)]"
          @input="formErrors.clear('profession')"
          :disabled="!editing"
          :maxlength="255"
          required/>
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-md-3">
        <span class="select is-fullwidth is-small">
          <va-checkbox
          :label="`${$t('clinical.people.is_pensioner')}?`"
          id="checkbox-is-pensioner"
          :isCircle="true"
          :disabled="!editing"
          v-model="form.is_pensioner"/>
        </span>
      </div>

      <div class="col-12 col-md-3">
        <span class="select is-fullwidth is-small">
          <va-checkbox
            :label="`${$t('clinical.people.is_deceased')}?`"
            id="checkbox-is-deceased"
            :isCircle="true"
            :disabled="!editing"
            v-model="form.is_deceased"/>
        </span>
      </div>

    </div>

    <div class="row">

      <div class="col-12 col-md-4">
        <va-input
          :placeholder="$t('clinical.people.nif')"
          v-model="form.tax_number"
          id="editing-nif"
          :error="formErrors.has('tax_number')"
          :error-messages="[$t(`${formErrors.get('tax_number')}`)]"
          @input="formErrors.clear('tax_number')"
          :disabled="!editing"
          :maxlength="25"
          required/>
      </div>

      <div class="col-12 col-md-8">
        <va-input
          :placeholder="$t('clinical.people.insurer')"
          v-model="form.insurer"
          id="editing-insurer"
          :error="formErrors.has('insurer')"
          :error-messages="[$t(`${formErrors.get('insurer')}`)]"
          @input="formErrors.clear('insurer')"
          :disabled="!editing"
          :maxlength="255"
          required/>
      </div>

      <!-- <div class="col-12 col-md-3">
        <va-input
          :placeholder="$t('clinical.people.source')"
          v-model="form.source"
          id="editing-source"
          :error="formErrors.has('source')"
          :error-messages="[$t(`${formErrors.get('source')}`)]"
          @input="formErrors.clear('source')"
          :disabled="!editing"
          required/>
      </div> -->

    </div>

    <div class="row">

      <div class="col-12">
        <va-input
          :placeholder="$t('clinical.people.obs')"
          v-model="form.obs"
          :maxlength="4000"
          id="editing-obs"
          :error="formErrors.has('obs')"
          :error-messages="[$t(`${formErrors.get('obs')}`)]"
          @input="formErrors.clear('obs')"
          :disabled="!editing"
          required/>
      </div>

    </div>

  </div>
</template>
<script>
import moment from 'moment'
import { mapState } from 'vuex'
import { VueTelInput } from 'vue-tel-input'
import Multiselect from 'vue-multiselect'
import Errors from '@/utils/StandardErrors'
// import { PersonService } from '@/api/AuthClinicalService'
import { EMAIL_REGEX, PHONE_REGEX, DATE_REGEX } from '@/utils/regex'

export default {
  components: {
    VueTelInput,
    Multiselect
  },
  props: {
    entity: {
      type: Object,
      required: false
    },
    selectedPerson: {
      type: Object,
      required: false
    },
    editing: {
      type: Boolean,
      required: false,
      default: false
    },
    formErrors: {
      type: Object,
      required: false,
      default: () => {
        return new Errors()
      }
    },
    service: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      genders: [
        { id: 'not_disclosed', label: this.$t('gender.not_disclosed') },
        { id: 'female', label: this.$t('gender.female') },
        { id: 'male', label: this.$t('gender.male') },
      ],
      vueTelInputRedrawIndex: 0,
      birthdate: null,
      form: {
        title: '',
        first_name: '',
        last_name: '',
        name: '',
        email: '',
        entity: null,
        phone: '',
        phone_prefix: '',
        mobile_phone: '',
        addresses: [
          {
            line1: '',
            line2: '',
            postal_code: '',
            city: ''
          }
        ],
        obs: '',
        birthdate: null,
        gender: null,
        is_pensioner: false,
        is_deceased: false,
        insurer: '',
        profession: '',
        doctor: '',
        tax_number: '',
        source: '',
        process_id: '',
        patient: 1,
      },
      invalidPhone: false,
      invalidMobilePhone: false
    }
  },
  created () {
    this.preFillForm()
  },
  methods: {
    telephoneValidation: function (data) {
      this.invalidPhone = !data.isValid
      if (data.isValid) {
        this.form.phone = data.number.international
      }
    },
    mobilePhoneValidation: function (data) {
      this.invalidMobilePhone = !data.isValid
      if (data.isValid) {
        this.form.mobile_phone = data.number.international
      }
    },
    preFillForm: function () {
      if (!this.selectedPerson) {
        if (this.entity) {
          this.form.entity = this.entity
        } else if (this.availableEntities.length) {
          let index = this.availableEntities.findIndex(entity => entity.id === this.$auth.user().entity.id)
          if (index !== -1) {
            this.form.entity = this.availableEntities[index]
          } else {
            this.form.entity = this.availableEntities[0]
          }
        }
        // this.form.gender = this.genders[0]
      } else {
        let formData = JSON.parse(JSON.stringify(this.selectedPerson))

        delete formData.clinical_questionnaires
        delete formData.life_styles
        delete formData.otoscopies
        delete formData.tonal_audiometries
        delete formData.vocal_audiometries
        delete formData.hearing_devices
        formData.gender = this.genders.find(gender => gender.id === formData.gender)
        if (this.selectedPerson.addresses.length === 0) {
          formData.addresses = [
            {
              line1: '',
              line2: '',
              postal_code: '',
              city: ''
            }
          ]
        } else {
          formData.addresses = this.selectedPerson.addresses.map(({ line1, line2, postal_code, city }) => {
            return {
              line1,
              line2,
              postal_code,
              city
            }
          })
        }
        if (this.selectedPerson.birthdate !== null && this.selectedPerson.birthdate !== undefined && this.selectedPerson.birthdate !== '' && this.selectedPerson.birthdate !== '0000-00-00 00:00:00') {
          this.birthdate = moment(this.selectedPerson.birthdate).format('DD/MM/YYYY')
        }

        this.form = formData
      }
    },
    getFormData: function () {
      let data = JSON.parse(JSON.stringify(this.form))
      if (data.entity) {
        data.entity_id = data.entity.id
      }
      if (data.phone) {
        data.phone = data.phone.replace(/\s/g, '')
      }
      if (data.gender) {
        data.gender = data.gender.id
      }
      return data
    }
  },
  computed: {
    ...mapState({
      'countries': (state) => state.countries
    }),
    availableEntities: function () {
      return this.service.entities.filter(entity => this.service.user_permissions.some(p => (p.entity_id === entity.id || p.entity_id === null) && p.access_type >= 2 && p.info_type === 100))
    },
    invalidEmail: function () {
      if (this.form.email) {
        return !this.form.email.match(EMAIL_REGEX)
      }
      return false
    },
    isBirthdateFormatMatch: function() {
      return this.birthdate && this.birthdate.match(DATE_REGEX)
    },
    invalidBirthdate: function () {
      if (this.birthdate) {
        return !this.isBirthdateFormatMatch || this.isBirthdateAfterToday
      }
      return false
    },
    isBirthdateAfterToday: function () {
      if(this.isBirthdateFormatMatch){
        return moment(this.birthdate, 'DD/MM/YYYY').isAfter()
      }
      return false
    },
    birthdateError: function () {
      return this.isBirthdateAfterToday
        ? this.$t('people.errors.birthdate_must_must_be_a_current_date_or_in_the_past')
        : this.$t('people.errors.birthdate_must_have_a_valid_format')
    },
    authUserEntity: function () {
      return this.$auth.user().entity
    },
    defaultCountrySelection: function () {
      if (this.authUserEntity && this.authUserEntity.country) {
        return this.authUserEntity.country
      }
      return this.$store.state.countries.default
    },
  },
  watch: {
    form: {
      handler: function (value) {
        this.$emit('input', value)
      },
      deep: true
    },
    'form.entity': function (newEntity) {
      this.vueTelInputRedrawIndex = this.vueTelInputRedrawIndex + 1
    },
    'selectedPerson': function () {
      this.preFillForm()
    },
    'birthdate': function (value) {
      if (value && value.length === 10 && !this.invalidBirthdate) {
        this.form.birthdate = this.moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD')
      } else {
        this.form.birthdate = value
      }
    }
  }
}
</script>

<style lang="scss">

  #editing-birthdate .va-message-list__message {
    word-break: break-word;
  }

  .red-border{
    .va-input__container{
      border: 1px solid red !important;
      border-radius: 0px;
    }
  }

</style>
